import {
  UsCbpEntryModeOfTransport,
  UsIorNumberType,
} from '__generated__/graphql';
import {
  TransportationBillExtractResult,
  DocumentExtractRequest,
  DocumentExtractService,
  EntryDataExtractResult,
} from './documentExtractService';
import { AnyObject } from '@xbcb/shared-types';

/**
 * Mock document extract service. Doesn't make any back-end calls.
 */
export class MockDocumentExtractService implements DocumentExtractService {
  public extractTransportationBill({
    documentName,
  }: DocumentExtractRequest): TransportationBillExtractResult {
    return {
      importerOfRecordName: 'IOR ACME US',
      modeOfTransport: 'AIR',
      masterBillNumber: '157-01743346',
      houseBillNumber: '17725143442',
      quantity: 100,
      grossWeight: 200,
      flightNumber: 'A1234',
      estimatedArrivalTime: '2025-08-11',
      portOfUnladingCode: 2704,
      importDate: '2025-08-11',
      firmsCode: 'Y258',
    };
  }

  /* eslint-disable max-lines-per-function */
  public extractEntryData(
    { documentName: commercialInvoiceDocumentName }: DocumentExtractRequest,
    { documentName: packingListDocumentName }: DocumentExtractRequest,
  ): EntryDataExtractResult {
    return {
      masterBills: [
        {
          number: '157-01743346',
          houseBills: [
            {
              number: '17725143442',
              quantity: 100,
            },
          ],
        },
      ],
      conveyance: {
        tripNumber: 'A1234',
        modeOfTransport: UsCbpEntryModeOfTransport.AIR,
        grossWeight: 200,
      },
      departure: {
        exportCountryCode: 'CN',
        exportDate: '2025-08-10',
      },
      consignee: {
        name: 'Amazon Fulfillment Center',
        address: {
          address: '410 Terry Ave N',
          city: 'Seattle',
          stateCode: 'WA',
          postalCode: '98198',
          countryCode: 'US',
        },
        iorNumber: {
          type: UsIorNumberType.EIN,
          value: '199900-02534',
        },
        usConsignee: {
          id: 'usConsignee_70c6b814-4b1b-4998-81df-52b7fcfbcb33',
          version: 1,
        },
      },
      buyer: {
        sameAsIor: true,
      },
      invoices: [
        {
          invoiceNumber: '1',
          value: { value: 2000.0, currency: 'USD' },
          seller: {
            name: 'Shenzhen Baishitong e-Commerce, Ltd',
            address: {
              address: 'Unit 42, 1367 San Antonia Ave',
              city: 'Mountain View',
              stateCode: 'CA',
              postalCode: '24564',
              countryCode: 'US',
            },
            mid: 'USSHEBAI1367MOU',
            related: false,
            supplier: {
              id: 'supplier_c7892b63-7190-475d-9738-391d13591d7e',
              version: 3,
            },
          },
          products: [
            this.createEntryProduct({
              quantity: 20,
              totalValueUsd: 100.0,
              description: 'Blue Hiking backpack',
              originCountryCode: 'CN',
              htsNumber: '4202.92.3120',
              additionalHtsNumbers: ['9903.88.03', '9903.01.24', '9903.01.25'],
              unitReportingQuantities: [
                { unit: 'NO', value: 1 },
                { unit: 'KG', value: 7 },
              ],
            }),
            this.createEntryProduct({
              // for HTS mismatch case
              quantity: 20,
              totalValueUsd: 100.0,
              description: 'Green Hiking backpack',
              originCountryCode: 'CN',
              htsNumber: '4202.92.0000',
              unitReportingQuantities: [
                { unit: 'NO', value: 1 },
                { unit: 'KG', value: 7 },
              ],
            }),
            this.createEntryProduct({
              quantity: 1000,
              totalValueUsd: 400.0,
              description: "Men's graphic Blue Sweatshirt",
              originCountryCode: 'CN',
              htsNumber: '6110.30.3041',
              additionalHtsNumbers: ['9903.88.15', '9903.01.24', '9903.01.25'],
              unitReportingQuantities: [
                { unit: 'DOZ', value: 0.08333 },
                { unit: 'KG', value: 0.4 },
              ],
            }),
            this.createEntryProduct({
              quantity: 1000,
              totalValueUsd: 400.0,
              description: "Men's graphic Black Sweatshirt",
              originCountryCode: 'CN',
              htsNumber: '6110.30.3041',
              additionalHtsNumbers: ['9903.88.15', '9903.01.24', '9903.01.25'],
              unitReportingQuantities: [
                { unit: 'DOZ', value: 0.08333 },
                { unit: 'KG', value: 0.4 },
              ],
            }),
            this.createEntryProduct({
              // Not enough info case
              quantity: 200,
              totalValueUsd: 1000.0,
              description: 'Plastic container',
              originCountryCode: 'CN',
              htsNumber: '3924.10.0000',
              unitReportingQuantities: [
                { unit: 'NO', value: 1 },
                { unit: 'KG', value: 0.2 },
              ],
            }),
          ],
        },
      ],
    };
  }
  /* eslint-enable max-lines-per-function */

  private createEntryProduct(props: {
    quantity: number;
    totalValueUsd: number;
    description: string;
    originCountryCode: string;
    htsNumber: string;
    additionalHtsNumbers?: string[];
    unitReportingQuantities?: Array<{ unit: string; value: number }>;
  }): AnyObject {
    return {
      quantity: props.quantity,
      totalValue: { value: props.totalValueUsd, currency: 'USD' },
      lines: [
        {
          description: props.description,
          origin: {
            countryCode: props.originCountryCode,
          },
          tariffs: [
            ...(props.additionalHtsNumbers || []).map((htsNumber) => ({
              htsNumber,
            })),
            {
              htsNumber: props.htsNumber,
              unitReportingQuantities: props.unitReportingQuantities,
              unitValue: {
                currency: 'USD',
                value: props.totalValueUsd / props.quantity,
              },
            },
          ],
          manufacturer: {
            name: 'Shenzhen Baishitong E-Commerce, Ltd',
            mid: 'USSHEBAI1367MOU',
            address: {
              address: 'Unit 42, 1367 San Antonia Ave',
              city: 'Mountain View',
              stateCode: 'CA',
              postalCode: '24564',
              countryCode: 'US',
            },
            supplier: {
              id: 'supplier_c7892b63-7190-475d-9738-391d13591d7e',
              version: 3,
            },
          },
        },
      ],
    };
  }
}
